import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSignOutAlt, faUserCircle, faCrown, faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import SuperAdminSidebar from './SuperAdminSidebar';
import request from '../../../../request';
import DownloadSpool from './DownloadSpool';
import DinamicRoute from '../../Core/DinamicRoute';

import '../Admin/TurneroAdmin.css';
import './SuperAdmin.css';

/**
 * SuperAdminPanel — Root layout for the Platform Super Admin.
 * Now acts as a Layout component using react-router-dom's Outlet.
 */
const SuperAdminPanel = ({ ipserv }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [dynamicMenu, setDynamicMenu] = useState([]);
    const [loadingMenu, setLoadingMenu] = useState(true);

    // 1. Stabilize Admin Profile
    const profile = useMemo(() => {
        try {
            const data = localStorage.getItem('dm_admin');
            return data ? JSON.parse(data) : {};
        } catch (e) {
            console.error("Error parsing admin profile", e);
            return {};
        }
    }, []);

    // 2. Robust Path Calculation
    const activeSubPath = useMemo(() => {
        const base = '/Core/SuperAdmin';
        const current = location.pathname;
        if (current === base || current === base + '/') return '';
        return current.replace(base + '/', '');
    }, [location.pathname]);

    // Fetch Dynamic Menu
    useEffect(() => {
        if (profile.ProfileId) {
            setLoadingMenu(true);
            request.Get(ipserv + `superadmin/menu/${profile.ProfileId}`, {}, r => {
                if (r.success) setDynamicMenu(r.data || []);
                setLoadingMenu(false);
            });
        } else {
            setLoadingMenu(false);
        }
    }, [profile.ProfileId, ipserv]);

    const handleLogout = () => {
        localStorage.clear();
        sessionStorage.clear();
        window.location.reload();
    };

    return (
        <div className="sa-layout">
            <SuperAdminSidebar
                activeTab={activeSubPath}
                isCollapsed={isCollapsed}
                toggleCollapse={() => setIsCollapsed(!isCollapsed)}
                dynamicMenu={dynamicMenu}
            />

            <main className="sa-main">
                <header className="sa-topbar">
                    <div className="sa-topbar-brand">
                        <div className="sa-authority-badge">
                            <FontAwesomeIcon icon={faCrown} />
                            DATAMANAGER
                        </div>
                        <div className="d-none d-xl-flex flex-column ms-2">
                            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-dim)', letterSpacing: '0.04em' }}>
                                Core Platform
                            </span>
                        </div>
                    </div>

                    <div className="sa-topbar-right d-flex align-items-center gap-3">
                        <div className="d-none d-xl-flex flex-column align-items-end" style={{ lineHeight: 1.3 }}>
                            <span style={{ fontSize: '0.85rem', fontWeight: 700, letterSpacing: '1px' }}>
                                {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                        </div>

                        <div className="d-flex align-items-center gap-2"
                            style={{ padding: '6px 14px', borderRadius: 10, background: '#f1f5f9', border: '1px solid var(--border-color)' }}>
                            <FontAwesomeIcon icon={faUserCircle} style={{ fontSize: '1.5rem', color: 'var(--primary)' }} />
                            <div className="d-none d-md-flex flex-column">
                                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-main)' }}>
                                    {profile.AdminNombre || 'Super Admin'}
                                </span>
                            </div>
                        </div>

                        <button className="sa-btn-danger" onClick={handleLogout} style={{ fontSize: '0.8rem' }}>
                            <FontAwesomeIcon icon={faSignOutAlt} className="me-2" /> Salir
                        </button>
                    </div>
                </header>

                <div className="sa-viewport">
                    <Outlet />
                </div>
            </main>
            <DownloadSpool />
        </div>
    );
};

export default SuperAdminPanel;
